"use client"

import { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { userSelect, homeScreenStatSelect, allRemoteSettingsSelect } from "../../../redux/selectors/index"
import { notify } from "../../../utility/Helper1"
import { authActions } from "../../../redux/actions"
import WrapUpP2p from "../p2p/layouts/WrapUpP2p"
import PinAuth from "../../../utility/customComponents/PinAuth"
import { BanknoteIcon, Wallet, X, Plus, Edit, AlertCircle, CheckCircle2 } from "lucide-react"

const PaymentMethod = () => {
    const site_settings = useSelector(allRemoteSettingsSelect);
    const dispatch = useDispatch()
    const userData = useSelector(userSelect)
    const finance = useSelector(homeScreenStatSelect)

    const [securityObj, setSecurityObj] = useState({})
    const [loading, setLoading] = useState(false)
    const [walletAddresses, setWalletAddresses] = useState({})
    const [updatingPaymentDetail, setUpdatingPaymentDetail] = useState(false)
    const [activeTab, setActiveTab] = useState("bank")
    const [showBankModal, setShowBankModal] = useState(false)
    const [showWalletModal, setShowWalletModal] = useState(false)
    const [notification, setNotification] = useState(null)
    const [bank, setBank] = useState(userData?.bank ?? "")
    const [accountName, setAccountName] = useState(userData?.accountName ?? "")
    const [accountNumber, setAccountNumber] = useState(userData?.accountNumber ?? "")
    const [iban, setIban] = useState(userData?.iban ?? "")
    const [selectedCurrency, setSelectedCurrency] = useState("USD") // Default currency

    // Determine which payment methods to show based on autoConfirm
    const showCrypto = site_settings?.plans?.some(el => el.mother?.autoConfirm == 1)
    const showBank = site_settings?.plans?.some(el => el.mother?.autoConfirm == 0)
console.log('site_settings?.plans', site_settings?.plans)
    // Set initial tab based on available payment methods
    useEffect(() => {
        if (showCrypto && !showBank) {
            setActiveTab("wallet")
        } else if (showBank && !showCrypto) {
            setActiveTab("bank")
        }
    }, [showCrypto, showBank])

    useEffect(() => {
        const cryptoWallets = {}
        userData?.bankdetails?.forEach((item) => {
            cryptoWallets[item.name] = item.value
        })
        setWalletAddresses(cryptoWallets)
    }, [userData])

    const updateSpecificCrypto = (cryptoName, value) => {
        setWalletAddresses((prev) => ({
            ...prev,
            [cryptoName]: value,
        }))
    }

    const showNotification = (message, type) => {
        setNotification({ message, type })
        setTimeout(() => setNotification(null), 3000)
    }

    const doAddressChange = (e) => {
        if (updatingPaymentDetail) return
        e.preventDefault()
        setLoading(true)
        setUpdatingPaymentDetail(true)

        const updateObject = { ...(securityObj ?? {}), ...walletAddresses }

        dispatch(
            authActions.onLogin(`account/account_change/1`, updateObject, (response) => {
                notify(response.msg, response.code)
                setUpdatingPaymentDetail(false)
                setLoading(false)
                if (response.code === 1) {
                    setShowWalletModal(false)
                    showNotification("Wallet addresses updated successfully", "success")
                } else {
                    showNotification("Failed to update wallet addresses", "error")
                }
            }),
        )
    }

    const doBankUpdate = (e) => {
        if (updatingPaymentDetail) return
        e.preventDefault()
        setLoading(true)
        setUpdatingPaymentDetail(true)

        const updateObject = {
            iban,
            bank,
            accountName,
            accountNumber,
            ...(securityObj ?? {}),
        }

        dispatch(
            authActions.onLogin(`account/account_change/0`, updateObject, (response) => {
                notify(response.msg, response.code)
                setLoading(false)
                setUpdatingPaymentDetail(false)
                if (response.code === 1) {
                    setShowBankModal(false)
                    showNotification("Bank details updated successfully", "success")
                } else {
                    showNotification("Failed to update bank details", "error")
                }
            }),
        )
    }

    const hasBankDetails = userData?.bank
    const hasWalletAddresses = userData?.bankdetails?.some((item) => item?.value)

    // If neither payment method should be shown, display a message
    if (!showCrypto && !showBank) {
        return (
            <WrapUpP2p pageTitle="Payment Methods">
                <div className="bg-green-50 min-h-screen">
                    <div className="bg-white border-b">
                        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                            <div className="py-6">
                                <h1 className="text-2xl font-bold text-gray-900">Payment Methods</h1>
                                <p className="mt-1 text-sm text-gray-500">No payment methods available for your selected currency</p>
                            </div>
                        </div>
                    </div>
                </div>
            </WrapUpP2p>
        )
    }

    return (
        <>
            <WrapUpP2p pageTitle="Payment Methods">
                <div className="bg-green-50 min-h-screen">
                    {/* Header */}
                    <div className="bg-white border-b">
                        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                            <div className="py-6">
                                <h1 className="text-2xl font-bold text-gray-900">Payment Methods</h1>
                                <p className="mt-1 text-sm text-gray-500">Manage your payment methods</p>
                            </div>
                        </div>
                    </div>

                    {/* Main Content */}
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                        {/* Tabs - Only show if both payment methods are available */}
                        {(showCrypto && showBank) && (
                            <div className="mb-8 border-b border-gray-200">
                                <div className="flex space-x-8">
                                    {showBank && (
                                        <button
                                            onClick={() => setActiveTab("bank")}
                                            className={`pb-4 px-1 border-b-2 font-medium text-sm ${activeTab === "bank"
                                                    ? "border-green-600 text-green-700"
                                                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                                                }`}
                                        >
                                            Bank Accounts
                                        </button>
                                    )}

                                    {showCrypto && (
                                        <button
                                            onClick={() => setActiveTab("wallet")}
                                            className={`pb-4 px-1 border-b-2 font-medium text-sm ${activeTab === "wallet"
                                                    ? "border-green-600 text-green-700"
                                                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                                                }`}
                                        >
                                            Crypto Wallets
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Bank Accounts Tab */}
                        {showBank && activeTab === "bank" && (
                            <div>
                                <div className="flex justify-between items-center mb-6">
                                    <h2 className="text-lg font-medium text-gray-900">Your Bank Accounts</h2>
                                    <button
                                        onClick={() => setShowBankModal(true)}
                                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                                    >
                                        <Plus className="w-4 h-4 mr-2" />
                                        {hasBankDetails ? "Edit Bank Account" : "Add Bank Account"}
                                    </button>
                                </div>

                                {hasBankDetails ? (
                                    <div className="bg-white shadow overflow-hidden sm:rounded-md">
                                        <ul className="divide-y divide-gray-200">
                                            <li>
                                                <div className="px-6 py-5">
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center">
                                                            <div className="flex-shrink-0 h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                                                                <BanknoteIcon className="h-6 w-6 text-green-600" />
                                                            </div>
                                                            <div className="ml-4">
                                                                <div className="text-sm font-medium text-gray-900">{userData.bank}</div>
                                                                <div className="text-sm text-gray-500">{userData.accountName}</div>
                                                                <div className="mt-1 flex items-center">
                                                                    <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                                        {userData.accountNumber}
                                                                    </span>
                                                                    {userData.iban && (
                                                                        <span className="ml-2 px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                                                                            IBAN: {userData.iban}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="flex space-x-2">
                                                            <button
                                                                onClick={() => setShowBankModal(true)}
                                                                className="inline-flex items-center p-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                                                            >
                                                                <Edit className="h-4 w-4" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </li>
                                        </ul>
                                    </div>
                                ) : (
                                    <div className="bg-white shadow overflow-hidden sm:rounded-md p-6 text-center">
                                        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                                            <BanknoteIcon className="h-6 w-6 text-green-600" />
                                        </div>
                                        <h3 className="mt-2 text-sm font-medium text-gray-900">No bank accounts</h3>
                                        <p className="mt-1 text-sm text-gray-500">Get started by adding a new bank account.</p>
                                        <div className="mt-6">
                                            <button
                                                onClick={() => setShowBankModal(true)}
                                                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                                            >
                                                <Plus className="w-4 h-4 mr-2" />
                                                Add Bank Account
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Crypto Wallets Tab */}
                        {showCrypto && activeTab === "wallet" && (
                            <div>
                                <div className="flex justify-between items-center mb-6">
                                    <h2 className="text-lg font-medium text-gray-900">Your Crypto Wallets</h2>
                                    <button
                                        onClick={() => setShowWalletModal(true)}
                                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                                    >
                                        <Plus className="w-4 h-4 mr-2" />
                                        {hasWalletAddresses ? "Update Wallets" : "Add Wallets"}
                                    </button>
                                </div>

                                {hasWalletAddresses ? (
                                    <div className="bg-white shadow overflow-hidden sm:rounded-md">
                                        <ul className="divide-y divide-gray-200">
                                            {userData?.bankdetails?.map((item, index) => {
                                                if (!item?.value) return null

                                                const image = finance?.find((finItem) => finItem.type === item.type)?.image

                                                return (
                                                    <li key={index}>
                                                        <div className="px-6 py-5">
                                                            <div className="flex items-center justify-between">
                                                                <div className="flex items-center">
                                                                    <div className="flex-shrink-0 h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                                                                        {image ? (
                                                                            <img
                                                                                src={image || "/placeholder.svg"}
                                                                                alt={item.name}
                                                                                className="h-8 w-8 rounded-full"
                                                                            />
                                                                        ) : (
                                                                            <Wallet className="h-6 w-6 text-green-600" />
                                                                        )}
                                                                    </div>
                                                                    <div className="ml-4">
                                                                        <div className="flex items-center">
                                                                            <div className="text-sm font-medium text-gray-900">{item.name}</div>
                                                                            <span className="ml-2 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                                                {item.abbr}
                                                                            </span>
                                                                        </div>
                                                                        <div className="text-sm text-gray-500 font-mono mt-1">
                                                                            {item.value.length > 20
                                                                                ? `${item.value.substring(0, 10)}...${item.value.substring(item.value.length - 10)}`
                                                                                : item.value}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </li>
                                                )
                                            })}
                                        </ul>
                                    </div>
                                ) : (
                                    <div className="bg-white shadow overflow-hidden sm:rounded-md p-6 text-center">
                                        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                                            <Wallet className="h-6 w-6 text-green-600" />
                                        </div>
                                        <h3 className="mt-2 text-sm font-medium text-gray-900">No crypto wallets</h3>
                                        <p className="mt-1 text-sm text-gray-500">Get started by adding your wallet addresses.</p>
                                        <div className="mt-6">
                                            <button
                                                onClick={() => setShowWalletModal(true)}
                                                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                                            >
                                                <Plus className="w-4 h-4 mr-2" />
                                                Add Wallet Addresses
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </WrapUpP2p>

            {/* Notification Toast */}
            {notification && (
                <div
                    className={`fixed bottom-4 right-4 z-50 flex items-center p-4 mb-4 w-full max-w-xs rounded-lg shadow ${notification.type === "success" ? "bg-green-50" : "bg-red-50"
                        }`}
                >
                    <div
                        className={`inline-flex flex-shrink-0 justify-center items-center w-8 h-8 rounded-lg ${notification.type === "success" ? "bg-green-100 text-green-500" : "bg-red-100 text-red-500"
                            }`}
                    >
                        {notification.type === "success" ? (
                            <CheckCircle2 className="w-5 h-5" />
                        ) : (
                            <AlertCircle className="w-5 h-5" />
                        )}
                    </div>
                    <div className="ml-3 text-sm font-normal">{notification.message}</div>
                    <button
                        type="button"
                        className="ml-auto -mx-1.5 -my-1.5 bg-white text-gray-400 hover:text-gray-900 rounded-lg focus:ring-2 focus:ring-gray-300 p-1.5 hover:bg-gray-100 inline-flex h-8 w-8"
                        onClick={() => setNotification(null)}
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
            )}

            {/* Bank Modal */}
            {showBankModal && (
                <div style={{zIndex: '9999999999'}} className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden">
                        <div className="bg-green-600 px-6 py-4">
                            <div className="flex justify-between items-center">
                                <h3 className="text-lg font-medium text-white">Manage Bank Details</h3>
                                <button onClick={() => setShowBankModal(false)} className="text-white hover:text-gray-200">
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                        </div>

                        <div className="px-6 py-4">
                            <form className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name</label>
                                    <input
                                        type="text"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                                        placeholder="Enter bank name"
                                        value={bank}
                                        onChange={(e) => setBank(e.target.value)}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Account Name</label>
                                    <input
                                        type="text"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                                        placeholder="Enter account name"
                                        value={accountName}
                                        onChange={(e) => setAccountName(e.target.value)}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Account Number</label>
                                    <input
                                        type="text"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                                        placeholder="Enter account number"
                                        value={accountNumber}
                                        onChange={(e) => setAccountNumber(e.target.value)}
                                    />
                                </div>

                                {1>2 && <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">IBAN or SWIFT Code</label>
                                    <input
                                        type="text"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                                        placeholder="Enter IBAN or SWIFT code"
                                        value={iban}
                                        onChange={(e) => setIban(e.target.value)}
                                    />
                                </div>}

                                <div className="bg-gray-50 p-4 rounded-md">
                                    <PinAuth resultingObjMethod={setSecurityObj} userData={userData} />
                                </div>
                            </form>
                        </div>

                        <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3">
                            <button
                                onClick={() => setShowBankModal(false)}
                                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-500"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={doBankUpdate}
                                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 flex items-center"
                                disabled={updatingPaymentDetail}
                            >
                                {updatingPaymentDetail ? (
                                    <>
                                        <svg
                                            className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                        >
                                            <circle
                                                className="opacity-25"
                                                cx="12"
                                                cy="12"
                                                r="10"
                                                stroke="currentColor"
                                                strokeWidth="4"
                                            ></circle>
                                            <path
                                                className="opacity-75"
                                                fill="currentColor"
                                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                            ></path>
                                        </svg>
                                        Processing...
                                    </>
                                ) : (
                                    "Save Changes"
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Wallet Modal */}
            {showWalletModal && (
                <div style={{zIndex: '9999999999'}} className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden">
                        <div className="bg-emerald-600 px-6 py-4">
                            <div className="flex justify-between items-center">
                                <h3 className="text-lg font-medium text-white">Update Wallet Addresses</h3>
                                <button onClick={() => setShowWalletModal(false)} className="text-white hover:text-gray-200">
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                        </div>

                        <div className="px-6 py-4">
                            <form className="space-y-4">
                                {userData?.bankdetails?.map((item, index) => (
                                    <div key={index}>
                                        <div className="flex items-center mb-1">
                                            <label className="block text-sm font-medium text-gray-700">{item.name} Wallet</label>
                                            <span className="ml-2 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                                {item.abbr}
                                            </span>
                                        </div>
                                        <input
                                            type="text"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                            placeholder={`Enter ${item.name} wallet address`}
                                            value={walletAddresses[item.name] || ""}
                                            onChange={(e) => updateSpecificCrypto(item.name, e.target.value)}
                                        />
                                    </div>
                                ))}

                                <div className="bg-gray-50 p-4 rounded-md">
                                    <PinAuth resultingObjMethod={setSecurityObj} userData={userData} />
                                </div>
                            </form>
                        </div>

                        <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3">
                            <button
                                onClick={() => setShowWalletModal(false)}
                                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-500"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={doAddressChange}
                                className="px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 flex items-center"
                                disabled={updatingPaymentDetail}
                            >
                                {updatingPaymentDetail ? (
                                    <>
                                        <svg
                                            className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                        >
                                            <circle
                                                className="opacity-25"
                                                cx="12"
                                                cy="12"
                                                r="10"
                                                stroke="currentColor"
                                                strokeWidth="4"
                                            ></circle>
                                            <path
                                                className="opacity-75"
                                                fill="currentColor"
                                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                            ></path>
                                        </svg>
                                        Processing...
                                    </>
                                ) : (
                                    "Save Changes"
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}

export default PaymentMethod